/**
 * CONSUME adapters: convert PowerSync `block` rows (where `content` travels as
 * a JSON string) into the `AuthoredBlock`s of the `learning-blocks` engine, and
 * run local-first grading of a lesson.
 */

import type { AuthoredBlock, BlockResponse } from "../learning-blocks/types";
import type { BlockRegistry } from "../learning-blocks/registry";
import {
  buildAttemptSubmission,
  gradeLessonAttempt,
  type LessonOutcome,
  toLessonOutcome,
} from "./progress";

/** A `block` row as local Drizzle/PowerSync reads it. */
export interface BlockRow {
  id: string;
  kind: string | null;
  content: unknown;
  weight: number | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Parses the `content` (JSON string or already an object) into the authoring `def`. */
export function parseBlockContent(content: unknown): Record<string, unknown> {
  if (content == null) {
    return {};
  }
  if (typeof content === "string") {
    try {
      const parsed: unknown = JSON.parse(content);
      return isRecord(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return isRecord(content) ? content : {};
}

/** Converts a `block` row into an engine `AuthoredBlock`. */
export function authoredBlockFromRow(row: BlockRow): AuthoredBlock {
  return {
    id: row.id,
    type: row.kind ?? "quiz",
    def: parseBlockContent(row.content),
    weight: row.weight ?? 1,
  };
}

export function authoredBlocksFromRows(rows: readonly BlockRow[]): AuthoredBlock[] {
  return rows.map(authoredBlockFromRow);
}

/**
 * Grades a lesson local-first (same logic the server re-verifies) and produces
 * the `LessonOutcome` for the UI + persistence + gamification.
 */
export function consumeLesson(params: {
  registry: BlockRegistry;
  blocks: readonly AuthoredBlock[];
  responses: BlockResponse[];
  lessonId: string;
  courseId: string;
  xpReward: number;
  startedAt?: number;
  finishedAt?: number;
  claimedScore?: number;
  courseCompleted?: boolean;
}): LessonOutcome {
  const submission = buildAttemptSubmission(params.lessonId, params.responses, {
    claimedScore: params.claimedScore,
    startedAt: params.startedAt,
    finishedAt: params.finishedAt,
  });
  const attempt = gradeLessonAttempt(params.registry, params.blocks, submission);
  return toLessonOutcome({
    lessonId: params.lessonId,
    courseId: params.courseId,
    xpReward: params.xpReward,
    attempt,
    courseCompleted: params.courseCompleted,
  });
}
